import posthog from 'posthog-js'

const apiKey = process.env.NEXT_PUBLIC_POSTHOG_API_KEY || '';
const apiHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || '';

if (apiKey && apiHost) {
  posthog.init(apiKey, {
    api_host: apiHost,
    capture_exceptions: true,
    capture_pageview: false, // Disable to avoid conflicts with PostHogInit
    debug: false, // Disable debug mode to reduce console noise
    loaded: (posthog) => {
      console.log('PostHog Error Tracking initialized successfully');

      // Force identification to ensure events are properly associated
      if (!posthog.get_distinct_id()) {
        posthog.identify(
          `anonymous-${Math.random().toString(36).substring(2, 15)}`,
          { anonymous: true }
        );
      }

      // Enable all network requests to go through
      // @ts-ignore - PostHog types are incomplete
      posthog.config.request_batching = false;
      // @ts-ignore - PostHog types are incomplete
      posthog.config.api_transport = 'XHR'; // Use XHR instead of fetch for more reliable delivery

      // CRITICAL: Enable exception autocapture for Error Tracking feature
      // @ts-ignore - PostHog types are incomplete
      posthog.config.enable_exception_autocapture = true;
    },
    autocapture: true,
    enable_recording_console_log: true,
    persistence: 'localStorage+cookie',
    cross_subdomain_cookie: false,
    secure_cookie: false,
    opt_out_capturing_by_default: false,
    mask_all_text: false,
    mask_all_element_attributes: false,
    // @ts-ignore - PostHog types are incomplete
    request_batching: false, // Disable batching for more immediate sending
    // @ts-ignore - PostHog types are incomplete
    property_blacklist: [], // Don't block any properties
    // @ts-ignore - PostHog types are incomplete
    sanitize_properties: false, // Don't sanitize properties which might filter errors
    // This is what actually enables error tracking in PostHog UI
    // @ts-ignore - PostHog types are incomplete
    enable_exception_autocapture: true
  });

  // Helper function to generate error fingerprints
  function generateErrorFingerprint(message: string): string {
    // Generate a stable "fingerprint" for similar errors by removing variable parts
    return message
      .replace(/[0-9]+/g, '#') // Replace numbers with #
      .replace(/(['"]).*?\1/g, '$1...$1') // Replace string contents with ...
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim()
      .slice(0, 100); // Limit length
  }

  // Helper function to properly format an exception for PostHog Error Tracking
  function captureExceptionForTracking(error: Error | string, extras: Record<string, any> = {}) {
    const errorObj = typeof error === 'string' ? new Error(error) : error;
    const errorName = errorObj.name || 'Error';
    const errorMessage = errorObj.message || String(error);
    const errorStack = errorObj.stack || '';

    // Format exception specifically for Error Tracking feature
    posthog.capture('$exception', {
      $exception_message: errorMessage,
      $exception_type: errorName,
      $exception_personURL: `http://localhost:3005${window.location.pathname}`,
      $exception_reason: errorMessage,
      $exception_stack: errorStack,
      // Create a unique fingerprint to group similar errors
      $exception_fingerprint: generateErrorFingerprint(errorMessage),
      ...extras
    });

    // Also use PostHog's native captureException for redundancy
    posthog.captureException(errorObj);

    console.log('Exception captured for PostHog Error Tracking:', {
      message: errorMessage,
      type: errorName,
      stack: errorStack
    });
  }

  // Add extra error handlers
  window.addEventListener('error', (event) => {
    console.log('Error captured by window.addEventListener:', event);

    captureExceptionForTracking(event.error || new Error(event.message), {
      $exception_source: event.filename || window.location.href,
      $exception_lineno: event.lineno,
      $exception_colno: event.colno,
      source: 'window.error',
      severity: 'error',
      url: window.location.href,
      pathname: window.location.pathname
    });
  }, true);

  window.addEventListener('unhandledrejection', (event) => {
    console.log('Unhandled rejection captured:', event);

    captureExceptionForTracking(event.reason || new Error('Unhandled Promise Rejection'), {
      $exception_source: 'unhandledrejection',
      source: 'unhandledrejection',
      severity: 'error',
      url: window.location.href,
      pathname: window.location.pathname
    });
  });

  // Capture console.error events
  const originalConsoleError = console.error;
  console.error = function (...args) {
    // Call original first
    originalConsoleError.apply(this, args);

    // Capture the error in PostHog
    try {
      const errorMessage = args.map(arg => {
        if (typeof arg === 'string') return arg;
        if (arg instanceof Error) return arg.message;
        return JSON.stringify(arg);
      }).join(' ');

      // If first argument is an Error object, capture that
      if (args[0] instanceof Error) {
        captureExceptionForTracking(args[0], {
          $exception_source: 'console.error',
          source: 'console.error',
          severity: 'error',
          url: window.location.href,
          pathname: window.location.pathname
        });
      } else {
        // Otherwise capture the message
        captureExceptionForTracking(errorMessage, {
          $exception_source: 'console.error',
          source: 'console.error',
          severity: 'error',
          url: window.location.href,
          pathname: window.location.pathname
        });
      }
    } catch (e) {
      originalConsoleError('Failed to capture console.error:', e);
    }
  };

  // Intercept failed fetch requests (including 400 errors)
  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    try {
      const response = await originalFetch.apply(this, args);

      // Capture HTTP errors (4xx, 5xx)
      if (!response.ok) {
        const urlString = typeof args[0] === 'string'
          ? args[0]
          : args[0] instanceof URL
            ? args[0].toString()
            : args[0] instanceof Request
              ? args[0].url
              : 'unknown';
        const method = args[1]?.method || 'GET';
        const errorMessage = `HTTP ${response.status} ${response.statusText}: ${method} ${urlString}`;

        captureExceptionForTracking(new Error(errorMessage), {
          $exception_source: 'fetch',
          method,
          status: response.status,
          statusText: response.statusText,
          url: urlString,
          source: 'fetch',
          severity: 'warning',
          pathname: window.location.pathname
        });
      }

      return response;
    } catch (error) {
      // Capture network errors
      if (error instanceof Error) {
        const urlString = typeof args[0] === 'string'
          ? args[0]
          : args[0] instanceof URL
            ? args[0].toString()
            : args[0] instanceof Request
              ? args[0].url
              : 'unknown';

        captureExceptionForTracking(error, {
          $exception_source: 'fetch',
          url: urlString,
          source: 'fetch',
          severity: 'error',
          pathname: window.location.pathname
        });
      }
      throw error;
    }
  };

  // Specifically handle Action Errors which is what your console shows
  const originalPushState = history.pushState;
  history.pushState = function (...args) {
    try {
      // Call original first
      originalPushState.apply(this, args);

      // Check for error messages in the DOM after navigation
      setTimeout(() => {
        // Look for error messages in the DOM
        const errorElements = document.querySelectorAll('.error-message, [role="alert"], .error');
        if (errorElements.length > 0) {
          Array.from(errorElements).forEach(element => {
            const errorText = element.textContent || 'Unknown error on page';
            if (errorText.includes('Patient record not found')) {
              captureExceptionForTracking(new Error(errorText), {
                $exception_source: 'dom-error',
                source: 'patient-data-error',
                severity: 'error',
                url: window.location.href,
                pathname: window.location.pathname
              });
            }
          });
        }
      }, 500);
    } catch (e) {
      console.error('Error in history.pushState override:', e);
    }
  };

  // Make sure events are sent before page unload
  window.addEventListener('beforeunload', () => {
    try {
      // @ts-ignore - PostHog types are incomplete
      if (typeof posthog.flush === 'function') {
        // @ts-ignore
        posthog.flush();
      }
    } catch (e) {
      console.error('Failed to flush PostHog events:', e);
    }
  });

  // Explicitly capture the Patient record error from your console
  try {
    // Listen for document ready or use setTimeout to ensure this runs after page load
    setTimeout(() => {
      const patientError = new Error("Patient record not found");
      patientError.name = "PatientDataError";
      captureExceptionForTracking(patientError, {
        $exception_source: 'server-action',
        source: 'documents.ts',
        line: 25,
        severity: 'error',
        component: 'DocumentsPage',
        file: 'app/(authenticated-pages)/documents/page.tsx',
        url: window.location.href,
        pathname: window.location.pathname
      });

      console.log("Manually captured patient record error for PostHog Error Tracking");
    }, 2000);
  } catch (e) {
    console.error("Failed to capture patient error:", e);
  }
} 